import { Divider, Flex } from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo } from 'react';

import type {
  BorrowingBaseMechanicsForLoanProduct,
  ExclusionSqlClauseState,
  PossibleLoanProductCriteria,
} from '../../types';
import { ExclusionSqlClauseBuilder } from './ExclusionSqlClauseBuilder';
import { createLoanProductOptions } from './utils';
import { colors } from 'packages/common-frontend/colors';
import { DeleteIconButton } from 'packages/common-frontend/components/IconButton';
import { Select } from 'packages/common-frontend/components/Select';

export const LoanProductCriteriaBlock = ({
  loanProductCriteria,
  setCurrentLoanProductCriteria,
  index,
  creditFacilityOptions,
  borrowingBaseMechanicsMap,
  validLoanProductsForCreditFacilitiesMap,
  currentExclusionSqlClauseStates,
  showExclusionSqlClauseRuleErrors,
  loanProductCriteriaCount,
  readOnly,
}: {
  loanProductCriteria: PossibleLoanProductCriteria;
  setCurrentLoanProductCriteria: React.Dispatch<
    React.SetStateAction<PossibleLoanProductCriteria[]>
  >;
  index: number;
  creditFacilityOptions: { value: string; label: string }[];
  borrowingBaseMechanicsMap: Map<string, BorrowingBaseMechanicsForLoanProduct>;
  validLoanProductsForCreditFacilitiesMap: Map<string, string[]>;
  currentExclusionSqlClauseStates: React.MutableRefObject<
    Map<number, ExclusionSqlClauseState>
  >;
  showExclusionSqlClauseRuleErrors: boolean;
  loanProductCriteriaCount: number;
  readOnly: boolean;
}) => {
  const loanProductOptions = createLoanProductOptions({
    validLoanProductsForCreditFacilitiesMap,
    borrowingBaseMechanicsMap,
    creditFacilityId: loanProductCriteria.creditFacilityId,
  });

  const borrowingBaseMechanicsBaseFields = useMemo(() => {
    if (
      loanProductCriteria.creditFacilityId &&
      loanProductCriteria.loanProductId
    ) {
      const borrowingBaseMechanicsForLoanProduct = borrowingBaseMechanicsMap.get(
        `${loanProductCriteria.creditFacilityId}+${loanProductCriteria.loanProductId}`,
      );
      return borrowingBaseMechanicsForLoanProduct?.baseFields;
    }
    return undefined;
  }, [
    borrowingBaseMechanicsMap,
    loanProductCriteria.creditFacilityId,
    loanProductCriteria.loanProductId,
  ]);

  const updateLoanProductCriteriaBlock = useCallback(
    (creditFacilityId?: string) => {
      setCurrentLoanProductCriteria((oldLoanProductCriteria) => {
        const newLoanProductCriteria = [...oldLoanProductCriteria];
        const newLoanProductCriteriaComponent = {
          ...newLoanProductCriteria[index],
        };
        newLoanProductCriteriaComponent.creditFacilityId = creditFacilityId;
        if (creditFacilityId) {
          const validLoanProducts =
            validLoanProductsForCreditFacilitiesMap.get(creditFacilityId) ?? [];
          if (validLoanProducts.length > 0) {
            newLoanProductCriteriaComponent.loanProductId =
              validLoanProducts[0];
          }
        } else {
          newLoanProductCriteriaComponent.loanProductId = undefined;
        }
        newLoanProductCriteria[index] = newLoanProductCriteriaComponent;
        return newLoanProductCriteria;
      });
    },
    [
      index,
      setCurrentLoanProductCriteria,
      validLoanProductsForCreditFacilitiesMap,
    ],
  );

  useEffect(() => {
    if (
      !loanProductCriteria.creditFacilityId &&
      creditFacilityOptions.length === 1
    ) {
      updateLoanProductCriteriaBlock(creditFacilityOptions[0].value);
    }
  }, [
    creditFacilityOptions,
    index,
    loanProductCriteria,
    setCurrentLoanProductCriteria,
    updateLoanProductCriteriaBlock,
  ]);

  return (
    <Flex>
      <Flex direction="column" width="925px">
        <Flex mb="32px">
          <Select
            value={loanProductCriteria.creditFacilityId}
            options={creditFacilityOptions}
            inputFloatingLabel="Credit Facility"
            inputFloatingLabelBackgroundColor={colors.white}
            onChange={(creditFacilityId) => {
              updateLoanProductCriteriaBlock(creditFacilityId);
            }}
            placeholderLabel="Select a credit facility"
            isDisabled={readOnly}
          />
        </Flex>
        {loanProductCriteria.creditFacilityId && (
          <Flex mb="32px">
            <Select
              value={loanProductCriteria.loanProductId}
              options={loanProductOptions}
              inputFloatingLabel="Loan Product"
              inputFloatingLabelBackgroundColor={colors.white}
              onChange={(loanProductId) => {
                setCurrentLoanProductCriteria((oldLoanProductCriteria) => {
                  const newLoanProductCriteria = [...oldLoanProductCriteria];
                  const newLoanProductCriteriaComponent = {
                    ...newLoanProductCriteria[index],
                  };
                  newLoanProductCriteriaComponent.loanProductId = loanProductId;
                  newLoanProductCriteria[
                    index
                  ] = newLoanProductCriteriaComponent;
                  return newLoanProductCriteria;
                });
              }}
              placeholderLabel="Select a loan product"
              isDisabled={readOnly}
            />
          </Flex>
        )}
        {borrowingBaseMechanicsBaseFields && (
          <ExclusionSqlClauseBuilder
            baseFields={borrowingBaseMechanicsBaseFields}
            initialExclusionSqlClause={loanProductCriteria.exclusionSqlClause}
            loanProductCriteriaIndex={index}
            currentExclusionSqlClauseStates={currentExclusionSqlClauseStates}
            showExclusionSqlClauseRuleErrors={showExclusionSqlClauseRuleErrors}
            readOnly={readOnly}
          />
        )}
        {(!readOnly || index < loanProductCriteriaCount - 1) && (
          <Divider mb="32px" />
        )}
      </Flex>
      {!readOnly && (
        <Flex ml="8px" color={colors.black[70]}>
          <DeleteIconButton
            ariaLabel="delete loan product criteria"
            label="Delete Loan Product Criteria"
            onClick={() => {
              setCurrentLoanProductCriteria((oldLoanProductCriteria) => {
                const newLoanProductCriteria = [...oldLoanProductCriteria];
                newLoanProductCriteria.splice(index, 1);
                return newLoanProductCriteria;
              });
              currentExclusionSqlClauseStates.current.delete(index);
            }}
            fontSize="2xl"
          />
        </Flex>
      )}
    </Flex>
  );
};
